"use client";

import { faCloudPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type InputHTMLAttributes, useState } from "react";
import { cn, normalizeFiles } from "../../..";

interface FileInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
	lang: string;
	onChange?: (files: FileList | null) => void;
	messages: {
		dragAndDrop: string;
		chooseFile: string;
	};
}

export function FileInput({ lang, messages, onChange, ...props }: FileInputProps) {
	const [drop, setDrop] = useState(false);
	const [files, setFiles] = useState<File[]>();

	function onAddFiles(newFiles: FileList | null) {
		setFiles(normalizeFiles(newFiles));
		onChange?.(newFiles);
	}

	return (
		<label
			className={cn(
				"group flex w-full cursor-pointer flex-wrap items-center justify-between gap-x-6 gap-y-4 rounded border enter:border-primary-medium border-dashed enter:bg-primary-light/50 px-6 py-4 outline-none motion-safe:transition-colors",
				{ "border-primary": !drop, "border-primary-medium bg-primary-light/50": drop },
			)}
			onDrop={(e) => {
				e.preventDefault();
				onAddFiles(e.dataTransfer.files);
				setDrop(false);
			}}
			onDragOver={(e) => {
				e.preventDefault();
				setDrop(true);
			}}
			onDragLeave={(e) => {
				e.preventDefault();
				setDrop(false);
			}}
		>
			<input
				className="hidden"
				type="file"
				onChange={(e) => onAddFiles(e.target.files)}
				{...props}
			/>

			<span className="flex items-center gap-4">
				<FontAwesomeIcon icon={faCloudPlus} className="text-2xl text-primary" />
				{files?.length ? (
					<span>
						{new Intl.ListFormat(lang, { type: "conjunction" }).format(
							files.map((file) => file.name),
						)}
					</span>
				) : (
					<span className="text-shades-dark">{messages.dragAndDrop}</span>
				)}
			</span>

			<div className="inline-block rounded border-2 border-primary bg-white px-4 py-2 text-center font-bold font-primary text-primary text-sm group-enter:border-primary-medium group-enter:text-primary-medium motion-safe:transition-colors">
				{messages.chooseFile}
			</div>
		</label>
	);
}
